import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Mail, Phone, Calendar, GraduationCap, Building2, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

export default function AdminStudents({ apiBase = '/api/admin', institutionScope = false }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterOptions, setFilterOptions] = useState({ institutions: [], courses: [] });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 2000);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    axios.get(`${apiBase}/users/filters`)
      .then((res) => setFilterOptions({
        institutions: res.data?.institutions || [],
        courses: res.data?.courses || [],
      }))
      .catch(() => toast.error('Failed to load student filters'));
  }, [apiBase]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, searchParams]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
      };
      if (debouncedSearch) params.q = debouncedSearch;
      ['institutionId', 'courseId', 'dateFrom', 'dateTo'].forEach((key) => {
        if (institutionScope && key === 'institutionId') return;
        const value = searchParams.get(key);
        if (value) params[key] = value;
      });
      const res = await axios.get(`${apiBase}/users`, { params });
      setUsers(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setTotalPages(Math.ceil((res.data?.total || 0) / limit) || 1);
    } catch (err) {
      toast.error('Failed to load users data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, debouncedSearch, searchParams]);

  const updateSearch = (value) => {
    setSearchTerm(value);
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value); else next.delete('q');
    setSearchParams(next);
  };

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next);
  };

  const resetFilters = () => {
    const next = new URLSearchParams(searchParams);
    ['institutionId', 'courseId', 'dateFrom', 'dateTo'].forEach((key) => next.delete(key));
    setSearchParams(next);
  };

  const handleExport = async () => {
    try {
      const params = { export: 'true' };
      if (debouncedSearch) params.q = debouncedSearch;
      ['institutionId', 'courseId', 'dateFrom', 'dateTo'].forEach((key) => {
        if (institutionScope && key === 'institutionId') return;
        const value = searchParams.get(key);
        if (value) params[key] = value;
      });
      const res = await axios.get(`${apiBase}/users`, { params });
      const usersToExport = res.data?.items || [];
      if (usersToExport.length === 0) return toast.error('No data to export');

      const headers = ['Student Name', 'DOB', 'Application ID', 'Status', 'Email', 'Mobile', 'Institution', 'Course', 'Registered On'];
      const rows = usersToExport.map(u => [
        u.name || 'Student',
        u.dob || '',
        u.applicationId || '',
        u.applicationStatus || '',
        u.email || '',
        u.mobile || '',
        u.institutionName || '',
        u.selectedCourse || '',
        u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN') : ''
      ]);

      const csv = [headers, ...rows].map(row => row.map(value => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `seatify_students_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export students');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 ${
            page === i
              ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600'
              : 'text-slate-900 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:outline-offset-0'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const filtered = users;

  const rolePath = user?.role === 'cluster_admin'
    ? 'cluster-admin'
    : user?.role === 'institution_admin'
      ? 'institution'
      : 'admin';

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Poppins' }}>Students Directory</h1>
          {/*   <p className="text-slate-500 mt-1">Manage onboarded students and accounts.</p> */}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Search students..." 
              value={searchTerm}
              onChange={(e) => updateSearch(e.target.value)}
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none w-64 md:w-80"
            />
          </div>
          <button 
            onClick={handleExport}
            className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl font-medium hover:bg-slate-800 transition-colors"
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${institutionScope ? 'md:grid-cols-4' : 'md:grid-cols-5'} gap-3 mb-6`}>
        {!institutionScope && (
          <select
            value={searchParams.get('institutionId') || ''}
            onChange={(e) => updateFilter('institutionId', e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm font-medium"
          >
            <option value="">All Institutions</option>
            {filterOptions.institutions.map((item) => (
              <option key={item.id || item._id} value={item.id || item._id}>{item.name}</option>
            ))}
          </select>
        )}
        <select
          value={searchParams.get('courseId') || ''}
          onChange={(e) => updateFilter('courseId', e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm font-medium"
        >
          <option value="">All Courses</option>
          {filterOptions.courses
            .filter((item) => !searchParams.get('institutionId') || item.institutionId === searchParams.get('institutionId'))
            .map((item) => (
              <option key={item.id || item._id} value={item.id || item._id}>{item.name}</option>
            ))}
        </select>
        <input
          type="date"
          value={searchParams.get('dateFrom') || ''}
          onChange={(e) => updateFilter('dateFrom', e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm font-medium"
        />
        <input
          type="date"
          value={searchParams.get('dateTo') || ''}
          onChange={(e) => updateFilter('dateTo', e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm font-medium"
        />
        <button
          onClick={resetFilters}
          className="px-4 py-2.5 border border-slate-200 rounded-xl bg-white text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          Clear Filters
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-sm">
                    <th className="p-4 font-bold">Student</th>
                    <th className="p-4 font-bold">Application</th>
                    <th className="p-4 font-bold">Contact Details</th>
                    <th className="p-4 font-bold">Institution & Course</th>
                    <th className="p-4 font-bold">Registered On</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan="5" className="p-8 text-center text-slate-500">No students found.</td></tr>
                  ) : (
                    filtered.map(student => (
                      <tr key={student._id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold">
                              {student.name?.[0]?.toUpperCase() || 'S'}
                            </div>
                            <div>
                              <p className="font-bold text-slate-800">{student.name || 'Student'}</p>
                              {student.dob && <p className="text-xs text-slate-500 flex items-center gap-1"><Calendar size={12}/> DOB: {student.dob}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          {student.applicationId ? (
                            <Link 
                              to={`/${rolePath}/admissions/${student.applicationId}`}
                              className="font-mono text-xs font-bold text-indigo-600 hover:underline"
                            >
                              {student.applicationId}
                            </Link>
                          ) : (
                            <p className="font-mono text-xs font-bold text-slate-400">-</p>
                          )}
                          <p className="text-xs text-slate-500 mt-1 capitalize">{String(student.applicationStatus || 'No application').replace(/_/g, ' ')}</p>
                        </td>
                        <td className="p-4">
                          {student.email && <p className="text-sm text-slate-600 flex items-center gap-1"><Mail size={14} className="text-slate-400"/> {student.email}</p>}
                          {student.mobile && <p className="text-sm text-slate-600 flex items-center gap-1 mt-1"><Phone size={14} className="text-slate-400"/> {student.mobile}</p>}
                        </td>
                        <td className="p-4">
                          <p className="text-sm text-slate-700 flex items-center gap-1 font-semibold"><Building2 size={14} className="text-slate-400"/> {student.institutionName || '-'}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><GraduationCap size={14} className="text-slate-400"/> {student.selectedCourse || '-'}</p>
                        </td>
                        <td className="p-4 text-sm text-slate-500">
                          {new Date(student.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6 rounded-b-2xl">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="relative ml-3 inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-slate-700">
                      Showing <span className="font-bold">{(page - 1) * limit + 1}</span> to{' '}
                      <span className="font-bold">{Math.min(page * limit, total)}</span> of{' '}
                      <span className="font-bold">{total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-xl shadow-sm bg-white border border-slate-200 overflow-hidden" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-3 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50 font-bold border-r border-slate-200"
                      >
                        &larr;
                      </button>
                      {renderPageNumbers()}
                      <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page >= totalPages}
                        className="relative inline-flex items-center px-3 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50 font-bold border-l border-slate-200"
                      >
                        &rarr;
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
